import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { isOnboardingPath } from "@/features/onboarding/routes";
import { useGetUserDetails } from "@/hooks/useGetUserDetails";

import { nextUnseenTutorial, tutorialsFor } from "../catalog";
import { TUTORIALS_PATH } from "../routes";
import {
  markTutorialsLater,
  markTutorialsSeen,
  readSeenTutorials
} from "../seen";
import type { Tutorial } from "../types";
import { TutorialToastCard } from "./TutorialToastCard";
import { TutorialsDialog } from "./TutorialsDialog";

/** Lets the page settle before the announcement slides in. */
export const POPUP_DELAY_MS = 1200;
export const TUTORIAL_TOAST_ID = "tutorial-announcement";

/**
 * Announces the newest tutorial the user hasn't seen, once, as a toast card
 * bottom left. Watch opens the player; Later leaves a dot on the account
 * avatar until they open Tutorials. Either way every tutorial they have is
 * marked seen. Not during the guided first run or on Tutorials itself.
 */
export const NewTutorialPopup: FC = () => {
  const {
    userDetails: { id: uid, userType }
  } = useGetUserDetails();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [announced, setAnnounced] = useState<Tutorial | null>(null);
  const [watching, setWatching] = useState<Tutorial | null>(null);
  const tutorials = useMemo(() => tutorialsFor(userType), [userType]);
  const onboarding = isOnboardingPath(pathname);
  const onTutorialsPage = pathname.startsWith(TUTORIALS_PATH);

  const markSeen = useCallback(
    () =>
      markTutorialsSeen(
        uid,
        tutorials.map((t) => t.id)
      ),
    [uid, tutorials]
  );

  useEffect(() => {
    if (!uid || onboarding || onTutorialsPage || announced) return undefined;
    const next = nextUnseenTutorial(userType, readSeenTutorials(uid));
    if (!next) return undefined;
    const timer = window.setTimeout(() => {
      setAnnounced(next);
      toast.custom(
        () => (
          <TutorialToastCard
            tutorial={next}
            onWatch={() => {
              toast.dismiss(TUTORIAL_TOAST_ID);
              markSeen();
              setWatching(next);
            }}
            onLater={() => {
              toast.dismiss(TUTORIAL_TOAST_ID);
              markSeen();
              markTutorialsLater(uid, [next.id]);
            }}
          />
        ),
        {
          id: TUTORIAL_TOAST_ID,
          duration: Infinity,
          position: "bottom-left",
          unstyled: true
        }
      );
    }, POPUP_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [uid, userType, onboarding, onTutorialsPage, announced, markSeen]);

  // Tutorials features the new one, so the card has said its piece.
  useEffect(() => {
    if (!announced || !onTutorialsPage) return;
    toast.dismiss(TUTORIAL_TOAST_ID);
    markSeen();
  }, [announced, onTutorialsPage, markSeen]);

  useEffect(
    () => () => {
      toast.dismiss(TUTORIAL_TOAST_ID);
    },
    []
  );

  if (!watching) return null;

  return (
    <TutorialsDialog
      open
      isNew
      tutorials={tutorials}
      initialId={watching.id}
      onOpenChange={(open) => {
        if (!open) setWatching(null);
      }}
      onWatchLater={() => markTutorialsLater(uid, [watching.id])}
      onBrowseAll={
        onTutorialsPage
          ? undefined
          : () => {
              setWatching(null);
              navigate(TUTORIALS_PATH);
            }
      }
    />
  );
};
